import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, ArrowRight, Activity, Cpu, Gauge, Bot, Camera, ShieldCheck, LineChart, Cloud, Wind, Video } from "lucide-react";
import { PublicLayout } from "../components/PublicLayout";
import { useLightbox } from "../components/Lightbox";

const fade = { hidden: { opacity: 0, y: 24 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }) };

const STATS = [
  ["3", "Phase Channels"], ["2Hz", "Live Sample Rate"], ["95%", "Cost Saving vs BMS"], ["24/7", "Cloud Uptime"],
];

const FEATURES = [
  [Activity, "3-Phase Metering", "Real-time voltage, current, power, PF, frequency & kWh from PZEM-004T sensors on every line."],
  [Gauge, "Live Gauges & LCD", "Animated gauges and a faithful 20×4 LCD mirror of the physical device display."],
  [Wind, "Air Quality Monitoring", "MQ-135 gas sensor reads CO₂, NH₃ and smoke - GOOD / POOR / DANGER label with live ppm readout and buzzer alert."],
  [Bot, "AI Energy Assistant", "Conversational assistant with voice & video call modes plus slash-command device control."],
  [Video, "AI Face Call", "Live face-to-face with Cortex - an animated AI robot that mirrors your expressions, converses naturally, and teaches topics via Gemini Vision."],
  [Camera, "Camera & Snapshots", "TTGO T-Camera streaming, snapshots and a browser-stored gallery archive."],
  [LineChart, "Grafana & ThingSpeak", "Historical time-series dashboards exported to Grafana Cloud, ThingSpeak & Sheets."],
  [Cloud, "Cloud Deployed", "Built on HiveMQ Cloud MQTT and deployed to Cloudflare Pages for global reach."],
];

const HW_LIST = [
  ["/assets/hw-front.jpeg", "Front Panel", "LCD · PIR · Camera · Light hub"],
  ["/assets/hw-lcd-top.jpeg", "Top - 20×4 LCD", "Live V · P · Temp readout"],
  ["/assets/hw-camera.jpeg", "Camera & Flash", "Lens · LED · TEMP vent"],
  ["/assets/hw-pir-ir.jpeg", "PIR & IR Receiver", "Motion sensing · IR remote"],
  ["/assets/hw-fan.jpeg", "Rear Cooling", "80mm fan · 9V & 12V inputs"],
  ["/assets/hw-beauty.jpeg", "Buzzer & Switch", "Audible alerts · toggle control"],
  ["/assets/hw-base.jpeg", "Ventilated Base", "Airflow grille · rubber feet"],
  ["/assets/hw-internal.jpeg", "Inside the Build", "ESP32 · PZEM · breadboard"],
  ["/assets/hw-panel.png", "Field Install", "Wired into a live 3-phase panel"],
];

const BOM_LIST = [
  ["/assets/kit-flatlay.png", "Component Kit - Flatlay", "All components before assembly"],
  ["/assets/bom-shopping.jpeg", "Enclosure Build - Shopping List", "Materials, glue & where to buy"],
  ["/assets/bom-tools.png", "Tools & Equipment", "Pricing, quantity & purpose"],
  ["/assets/bom-overview.png", "Components Overview", "Have · source · still missing"],
  ["/assets/diagrams/diag-bom-table.png", "AC Load Setup - BOM Table", "Lamp holders · LED bulbs · suppliers · prices"],
];

export default function Landing() {
  const hw  = useLightbox();
  const bom = useLightbox();
  const hwImages  = HW_LIST.map(([src, title, desc]) => ({ src, title, desc }));
  const bomImages = BOM_LIST.map(([src, title, desc]) => ({ src, title, desc }));

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] rounded-full bg-[var(--accent)]/10 blur-[120px]" />
          <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] rounded-full bg-[var(--accent)]/5 blur-[120px]" />
        </div>
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.div initial="hidden" animate="show" variants={fade} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[var(--bd)] bg-[var(--surface2)] text-xs font-mono text-[var(--t2)] mb-6">
              <span className="w-2 h-2 rounded-full bg-[var(--accent)] pulse-dot" /> CPUT Final Year Project · 2026
            </motion.div>
            <motion.h1 initial="hidden" animate="show" custom={1} variants={fade} className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
              Monitor campus energy<br /><span className="text-[var(--accent)]">in real time.</span>
            </motion.h1>
            <motion.p initial="hidden" animate="show" custom={2} variants={fade} className="text-base sm:text-lg text-[var(--t2)] mt-5 max-w-lg leading-relaxed">
              An IoT-based energy and power quality monitoring system for campus buildings - originally scoped as a basic metering dashboard, expanded into a full command center with environmental sensing, remote hardware control, camera surveillance, and AI-assisted analytics. Powered by ESP32, PZEM-004T sensors, and HiveMQ Cloud MQTT.
            </motion.p>
            <motion.div initial="hidden" animate="show" custom={3} variants={fade} className="flex flex-wrap gap-3 mt-8">
              <Link to="/register" data-testid="hero-get-started" className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[var(--accent)] text-black font-semibold active:scale-95 transition-transform">
                Get Started <ArrowRight size={18} />
              </Link>
              <Link to="/login" data-testid="hero-sign-in" className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-[var(--bd)] font-semibold hover:border-[var(--bd-strong)] transition-colors">
                Sign In
              </Link>
            </motion.div>
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-x-8 gap-y-4 mt-12">
              {STATS.map(([v, l]) => (
                <div key={l}>
                  <div className="font-mono font-bold text-2xl text-[var(--accent)]">{v}</div>
                  <div className="text-xs uppercase tracking-wider text-[var(--t2)]">{l}</div>
                </div>
              ))}
            </div>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }} className="relative">
            <div className="rounded-2xl overflow-hidden border border-[var(--bd)] shadow-2xl">
              <img src="/assets/hw-panel.png" alt="Campus Energy Monitor - live 3-phase panel install" className="w-full h-auto" data-testid="hero-banner" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="border-t border-[var(--bd)] bg-[var(--surface)]/30">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="uppercase tracking-[0.2em] text-[10px] font-bold text-[var(--accent)]">Capabilities</span>
            <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight mt-2">Everything an energy operator needs</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map(([Icon, title, desc], i) => (
              <motion.div key={title} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fade}
                className="p-6 rounded-xl bg-[var(--surface)] border border-[var(--bd)] hover:border-[var(--bd-strong)] transition-colors">
                <div className="w-11 h-11 rounded-lg bg-[var(--accent-soft)] text-[var(--accent)] flex items-center justify-center mb-4"><Icon size={20} /></div>
                <h3 className="font-heading font-bold text-lg">{title}</h3>
                <p className="text-sm text-[var(--t2)] mt-2 leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HARDWARE */}
      <section className="border-t border-[var(--bd)] bg-[var(--surface)]/30">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="uppercase tracking-[0.2em] text-[10px] font-bold text-[var(--accent)]">The Hardware</span>
            <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight mt-2">Engineered enclosure, v1.0</h2>
            <p className="text-[var(--t2)] mt-3">A custom-built enclosure housing the ESP32-WROOM-32, three PZEM-004T v3.0 meters with split-core CTs, DS18B20 temperature probe, MQ-135 gas sensor, TIP120-driven PWM fan, 20×4 I²C LCD, AM312 PIR, 5V buzzer, IR receiver, and TTGO T-Camera with OV2640 lens.</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {hwImages.map(({ src, title, desc }, i) => (
              <motion.div key={title} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fade}
                className="group rounded-xl overflow-hidden border border-[var(--bd)] bg-[var(--surface)] hover:border-[var(--accent)] transition-colors cursor-pointer" data-testid={`hardware-card-${i}`} onClick={() => hw.open(hwImages, i)}>
                <div className="aspect-square overflow-hidden bg-black">
                  <img src={src} alt={title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                </div>
                <div className="p-4">
                  <h3 className="font-heading font-bold text-sm sm:text-base">{title}</h3>
                  <p className="text-xs text-[var(--t2)] mt-0.5 font-mono">{desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Build documentation */}
          <div className="mt-14">
            <div className="text-center max-w-2xl mx-auto mb-8">
              <span className="uppercase tracking-[0.2em] text-[10px] font-bold text-[var(--accent)]">Build Documentation</span>
              <h3 className="font-heading font-black text-2xl sm:text-3xl tracking-tight mt-2">Bill of materials & tooling</h3>
              <p className="text-[var(--t2)] mt-2 text-sm">Sourced locally in Cape Town - click any sheet to view full size.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {bomImages.map(({ src, title, desc }, i) => (
                <div key={title} data-testid={`bom-card-${i}`} onClick={() => bom.open(bomImages, i)}
                  className="group rounded-xl overflow-hidden border border-[var(--bd)] bg-[var(--surface)] hover:border-[var(--accent)] transition-colors cursor-pointer">
                  <div className="aspect-[4/3] overflow-hidden bg-white">
                    <img src={src} alt={title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]" loading="lazy" />
                  </div>
                  <div className="p-4">
                    <h4 className="font-heading font-bold text-sm sm:text-base">{title}</h4>
                    <p className="text-xs text-[var(--t2)] mt-0.5 font-mono">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[var(--bd)]">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-16 text-center">
          <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">Ready to take control?</h2>
          <p className="text-[var(--t2)] mt-3">Create an account and open the live dashboard in seconds.</p>
          <Link to="/register" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md bg-[var(--accent)] text-black font-semibold mt-7 active:scale-95 transition-transform">
            <Zap size={18} /> Launch Dashboard
          </Link>
        </div>
      </section>
      {hw.node}
      {bom.node}
    </PublicLayout>
  );
}
